import { AMOUNT_FORMAT, toExcelDate } from 'components/xlsx/excelCells';
import type ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

import { OpenInvoiceRow } from 'types/openInvoices';
import { t } from 'utils/getTranslations';

type Cell = string | number | null;

const EXCLUDED_RECEIVABILITY = ['WRITTEN_OFF', 'IRRECOVERABLE'];

/** 1-based column indices (G..J) that hold money amounts. */
const AMOUNT_COLUMN_INDICES = [7, 8, 9, 10];

/** 1-based column index for the amount in the two-column reconciliation block. */
const RECONCILIATION_AMOUNT_COLUMN_INDEX = 2;

const round2 = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100;

const isExcluded = (row: OpenInvoiceRow): boolean => EXCLUDED_RECEIVABILITY.includes(row.receivabilityTypeCode);

/**
 * Formats an ISO date (yyyy-MM-dd) as DD.MM.YYYY for display. Returns an empty string when absent.
 */
const formatDisplayDate = (isoDate: string | null | undefined): string => {
    if (!isoDate) {
        return '';
    }
    const [year, month, day] = isoDate.split('-');
    if (!year || !month || !day) {
        return isoDate;
    }
    return `${day}.${month}.${year}`;
};

type Totals = { invoiced: number; paid: number; credited: number; open: number };

const emptyTotals = (): Totals => ({ invoiced: 0, paid: 0, credited: 0, open: 0 });

/**
 * A row whose money amounts are pre-rounded to 2 decimals. Rounding once up front means the exported
 * cells and the subtotals/grand total aggregate the exact same values, so totals tie to the visible
 * rows instead of drifting from summed raw values.
 */
type RoundedRow = OpenInvoiceRow;

const roundRow = (row: OpenInvoiceRow): RoundedRow => ({
    ...row,
    invoicedBaseCurrency: round2(row.invoicedBaseCurrency),
    paidBaseCurrency: round2(row.paidBaseCurrency),
    creditedBaseCurrency: round2(row.creditedBaseCurrency),
    openBaseCurrency: round2(row.openBaseCurrency),
});

const addToTotals = (totals: Totals, row: RoundedRow): void => {
    totals.invoiced += row.invoicedBaseCurrency;
    totals.paid += row.paidBaseCurrency;
    totals.credited += row.creditedBaseCurrency;
    totals.open += row.openBaseCurrency;
};

const totalsCells = (totals: Totals): Cell[] => [totals.invoiced, totals.paid, totals.credited, totals.open];

/** A payer group keyed by the payer identity, retaining the payer display name for rendering. */
type PayerGroup = { payer: string; rows: RoundedRow[] };

const groupByPayer = (rows: RoundedRow[]): Map<number, PayerGroup> => {
    const groups = new Map<number, PayerGroup>();
    rows.forEach((row) => {
        const existing = groups.get(row.payerId);
        if (existing) {
            existing.rows.push(row);
        } else {
            groups.set(row.payerId, { payer: row.payer, rows: [row] });
        }
    });
    return groups;
};

/** Applies the amount number format to the given amount columns of a worksheet row. */
const formatAmountCells = (row: ExcelJS.Row, columnIndices: number[]): void => {
    columnIndices.forEach((columnIndex) => {
        row.getCell(columnIndex).numFmt = AMOUNT_FORMAT;
    });
};

/**
 * Renders one section's body: rows grouped by payer (each with its own bold payer-total subtotal
 * row), returning the section's overall totals so the caller can render a section-total row.
 */
const renderPayerGroups = (worksheet: ExcelJS.Worksheet, sectionRows: RoundedRow[]): Totals => {
    const sectionTotal = emptyTotals();

    groupByPayer(sectionRows).forEach(({ payer, rows: groupRows }) => {
        const groupTotal = emptyTotals();
        groupRows.forEach((row, index) => {
            addToTotals(groupTotal, row);
            addToTotals(sectionTotal, row);
            const dataRow = worksheet.addRow([
                index === 0 ? payer : '',
                row.matters,
                row.responsible,
                row.invoiceNumber,
                toExcelDate(row.entryDate),
                toExcelDate(row.dueDate),
                row.invoicedBaseCurrency,
                row.paidBaseCurrency,
                row.creditedBaseCurrency,
                row.openBaseCurrency,
            ]);
            formatAmountCells(dataRow, AMOUNT_COLUMN_INDICES);
        });
        const subtotalRow = worksheet.addRow([
            t('open-invoices.payer-total', { payer }),
            '',
            '',
            '',
            '',
            '',
            ...totalsCells(groupTotal),
        ]);
        subtotalRow.font = { bold: true };
        formatAmountCells(subtotalRow, AMOUNT_COLUMN_INDICES);
    });

    return sectionTotal;
};

/**
 * Builds the "Open Invoices by Business Unit" AR-aging workbook from the flat per-invoice rows and
 * triggers a download. The active receivable excludes WRITTEN_OFF / IRRECOVERABLE rows; those are
 * reported separately in the reconciliation block so the filed-CSI basis reconciles.
 *
 * Rendered with exceljs so amount and date columns are real number and date cells, shown in the viewer's
 * regional format, and the header, subtotal, grand-total and reconciliation-total rows are styled bold.
 */
export const generateOpenInvoicesExcel = async (
    rows: OpenInvoiceRow[],
    asOfDate: string,
    includeWrittenOff: boolean
): Promise<void> => {
    const ExcelJS = (await import('exceljs')).default;

    const roundedRows = rows.map(roundRow);
    const activeRows = roundedRows.filter((row) => !isExcluded(row));
    const excludedRows = roundedRows.filter(isExcluded);

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(t('open-invoices.sheet-name'));

    worksheet.columns = [
        { width: 32 },
        { width: 18 },
        { width: 20 },
        { width: 14 },
        { width: 12 },
        { width: 12 },
        { width: 14 },
        { width: 14 },
        { width: 14 },
        { width: 14 },
    ];

    worksheet.addRow([t('open-invoices.report-title')]);
    worksheet.addRow([`${t('open-invoices.as-of')}: ${formatDisplayDate(asOfDate)}`]);
    worksheet.addRow([]);
    const headerRow = worksheet.addRow([
        t('open-invoices.column.payer'),
        t('open-invoices.column.matter'),
        t('open-invoices.column.responsible'),
        t('open-invoices.column.invoice'),
        t('open-invoices.column.entry-date'),
        t('open-invoices.column.due-date'),
        t('open-invoices.column.invoiced'),
        t('open-invoices.column.paid'),
        t('open-invoices.column.credited'),
        t('open-invoices.column.open'),
    ]);
    headerRow.font = { bold: true };

    const grandTotal = renderPayerGroups(worksheet, activeRows);

    const grandTotalRow = worksheet.addRow([
        t('open-invoices.grand-total'),
        '',
        '',
        '',
        '',
        '',
        ...totalsCells(grandTotal),
    ]);
    grandTotalRow.font = { bold: true };
    formatAmountCells(grandTotalRow, AMOUNT_COLUMN_INDICES);

    let writtenOffOpen: number;
    if (includeWrittenOff) {
        worksheet.addRow([]);
        const sectionTitleRow = worksheet.addRow([t('open-invoices.written-off-section-title')]);
        sectionTitleRow.font = { bold: true };

        const writtenOffTotal = renderPayerGroups(worksheet, excludedRows);

        const writtenOffTotalRow = worksheet.addRow([
            t('open-invoices.payer-total', { payer: t('open-invoices.written-off-section-title') }),
            '',
            '',
            '',
            '',
            '',
            ...totalsCells(writtenOffTotal),
        ]);
        writtenOffTotalRow.font = { bold: true };
        formatAmountCells(writtenOffTotalRow, AMOUNT_COLUMN_INDICES);
        writtenOffOpen = writtenOffTotal.open;
    } else {
        writtenOffOpen = excludedRows.reduce((sum, row) => sum + row.openBaseCurrency, 0);
    }

    worksheet.addRow([]);

    const activeReceivableRow = worksheet.addRow([
        t('open-invoices.reconciliation.active-receivable'),
        grandTotal.open,
    ]);
    formatAmountCells(activeReceivableRow, [RECONCILIATION_AMOUNT_COLUMN_INDEX]);

    const writtenOffRow = worksheet.addRow([
        t('open-invoices.reconciliation.written-off-excluded', { count: String(excludedRows.length) }),
        writtenOffOpen,
    ]);
    formatAmountCells(writtenOffRow, [RECONCILIATION_AMOUNT_COLUMN_INDEX]);

    const reconciliationTotalRow = worksheet.addRow([
        t('open-invoices.reconciliation.open-incl-written-off'),
        grandTotal.open + writtenOffOpen,
    ]);
    reconciliationTotalRow.font = { bold: true };
    formatAmountCells(reconciliationTotalRow, [RECONCILIATION_AMOUNT_COLUMN_INDEX]);

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    saveAs(blob, `open-invoices-${asOfDate}.xlsx`);
};
